import React, { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { format } from 'date-fns'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/ui/table'
import { Badge } from '~/ui/badge'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Calendar, CalendarDays, Music, Pause, Play, Square, Volume2 } from 'lucide-react'
import { buildPlaybackEventsVariables } from '~/reporting/components/playback-events-filters'

const PLAYBACK_EVENTS_QUERY = gql(`
  query PlaybackEvents(
    $organizationId: ID
    $zoneId: ID
    $deviceId: ID
    $trackId: ID
    $eventTypes: [String!]
    $startDate: ISO8601DateTime
    $endDate: ISO8601DateTime
    $limit: Int
    $offset: Int
  ) {
    playbackEvents(
      organizationId: $organizationId
      zoneId: $zoneId
      deviceId: $deviceId
      trackId: $trackId
      eventTypes: $eventTypes
      startDate: $startDate
      endDate: $endDate
      limit: $limit
      offset: $offset
    ) {
      id
      eventType
      organizationId
      zoneId
      deviceId
      trackId
      positionMs
      durationMs
      volume
      createdAt
      updatedAt
      trackTitle
      trackArtist
      playbackPercentage
      formattedPosition
      formattedDuration
      isTrackStarted
      isTrackEnded
      isPlaybackState
      isProgressEvent
      zone {
        id
        name
      }
      track {
        id
        name
        displayArtist
      }
    }
  }
`)

interface PlaybackEventsListProps {
  organizationId?: string
  zoneId?: string
  deviceId?: string
  trackId?: string
  defaultLimit?: number
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  'status.track_started': 'Track Started',
  'status.track_ended': 'Track Ended',
  'status.playing': 'Playing',
  'status.paused': 'Paused',
  'status.stopped': 'Stopped',
  // 'status.track_progress': 'Progress',
  // 'status.volume_changed': 'Volume Changed',
  'status.playlist_loaded': 'Playlist Loaded',
  'status.seeking': 'Seeking',
  // 'status.heartbeat': 'Heartbeat',
  // 'status.periodic_update': 'Periodic Update'
}

const EVENT_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'status.track_started': Play,
  'status.track_ended': Square,
  'status.playing': Play,
  'status.paused': Pause,
  'status.stopped': Square,
  'status.track_progress': Music,
  'status.volume_changed': Volume2,
  'status.playlist_loaded': Music,
  'status.seeking': Music,
  'status.heartbeat': Calendar,
  'status.periodic_update': CalendarDays,
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  'status.track_started': 'bg-green-100 text-green-800',
  'status.track_ended': 'bg-red-100 text-red-800',
  'status.playing': 'bg-blue-100 text-blue-800',
  'status.paused': 'bg-yellow-100 text-yellow-800',
  'status.stopped': 'bg-gray-100 text-gray-800',
  'status.track_progress': 'bg-purple-100 text-purple-800',
  'status.volume_changed': 'bg-orange-100 text-orange-800',
  'status.playlist_loaded': 'bg-indigo-100 text-indigo-800',
  'status.seeking': 'bg-pink-100 text-pink-800',
  'status.heartbeat': 'bg-emerald-100 text-emerald-800',
  'status.periodic_update': 'bg-teal-100 text-teal-800',
}

export const PlaybackEventsList: React.FC<PlaybackEventsListProps> = ({
  organizationId,
  zoneId,
  deviceId,
  trackId,
  defaultLimit = 50,
}) => {
  const [filters, setFilters] = useState({
    eventType: '',
    startDate: '',
    endDate: '',
    limit: defaultLimit,
    offset: 0,
  })

  const [searchQuery, setSearchQuery] = useState('')

  const { data, loading, error, refetch } = useQuery(PLAYBACK_EVENTS_QUERY, {
    variables: buildPlaybackEventsVariables({
      organizationId,
      zoneId,
      deviceId,
      trackId,
    }, filters),
    fetchPolicy: 'cache-and-network',
  })

  const filteredEvents = useMemo(() => {
    if (!data?.playbackEvents) return []

    return data.playbackEvents.filter((event) => {
      if (!searchQuery) return true

      const searchLower = searchQuery.toLowerCase()
      return (
        event.trackTitle?.toLowerCase().includes(searchLower) ||
        event.trackArtist?.toLowerCase().includes(searchLower) ||
        event.zone?.name?.toLowerCase().includes(searchLower) ||
        EVENT_TYPE_LABELS[event.eventType]?.toLowerCase().includes(searchLower)
      )
    })
  }, [data?.playbackEvents, searchQuery])

  const handleFilterChange = (
    key: 'eventType' | 'startDate' | 'endDate' | 'limit' | 'offset',
    value: string | number
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: key !== 'offset' ? 0 : Number(value), // Reset offset when other filters change
    }))
  }

  const handleLoadMore = () => {
    setFilters((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }))
  }

  const formatEventTime = (timestamp: string) => {
    return format(new Date(timestamp), 'MMM d, yyyy h:mm:ss a')
  }

  const getEventIcon = (eventType: string) => {
    const IconComponent = EVENT_TYPE_ICONS[eventType] || Music
    return <IconComponent className="h-4 w-4" />
  }

  if (loading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return <div className="p-4 text-red-600">Error loading playback events: {error.message}</div>
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 rounded-lg bg-gray-50 p-4">
        <div className="min-w-64 flex-1">
          <Input
            placeholder="Search by track, artist, or zone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        <Select
          value={filters.eventType}
          onValueChange={(value) => handleFilterChange('eventType', value)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Event Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="datetime-local"
          value={filters.startDate}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
          className="w-48"
          placeholder="Start Date"
        />

        <Input
          type="datetime-local"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
          className="w-48"
          placeholder="End Date"
        />

        <Select
          value={filters.limit.toString()}
          onValueChange={(value) => handleFilterChange('limit', parseInt(value))}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="200">200</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={() => refetch()} variant="outline" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Events Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Time</TableHead>
              <TableHead className="w-40">Event Type</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Zone</TableHead>
              <TableHead className="w-24">Position</TableHead>
              <TableHead className="w-24">Volume</TableHead>
              <TableHead className="w-24">Progress</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-gray-500">
                  No playback events found
                </TableCell>
              </TableRow>
            ) : (
              filteredEvents.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-mono text-sm">
                    {formatEventTime(event.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={`${EVENT_TYPE_COLORS[event.eventType]} flex items-center gap-1`}
                    >
                      {getEventIcon(event.eventType)}
                      {EVENT_TYPE_LABELS[event.eventType] || event.eventType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {event.trackTitle && (
                      <div>
                        <div className="font-medium">{event.trackTitle}</div>
                        <div className="text-sm text-gray-500">{event.trackArtist}</div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{event.zone?.name}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {event.formattedPosition || '-'}
                  </TableCell>
                  <TableCell>
                    {event.volume !== null && event.volume !== undefined ? (
                      <div className="flex items-center gap-1">
                        <Volume2 className="h-3 w-3" />
                        {event.volume}%
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {event.playbackPercentage !== null && event.playbackPercentage !== undefined ? (
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-600"
                          style={{ width: `${Math.min(event.playbackPercentage, 100)}%` }}
                        />
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Load More */}
      {data?.playbackEvents?.length === filters.limit && (
        <div className="flex justify-center">
          <Button onClick={handleLoadMore} variant="outline" disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}

      {/* Summary */}
      <div className="text-center text-sm text-gray-500">
        Showing {filteredEvents.length} events
        {filters.offset > 0 && ` (starting from ${filters.offset})`}
      </div>
    </div>
  )
}
